import { FC, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import Table from '@components/table';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import Info from '@components/common/info';
import { TPayee } from '@xeppt/xeppt-sdk/types';
import { usePayBillsModal } from '@hooks/modals/usePayBillsModal';
import Input from '@components/common/input';
import { routes } from '@const/routes';
import { useNavigate } from 'react-router-dom';
import emptyIllustration from '@svg/illustrations/empty_payees.svg';
import { formatDate } from '@utils/index';
import { useLocales } from '@hooks/helpers/useLocales';
import Skeleton from '@components/common/skeleton';

interface IProps {
    onAdd: () => void;
    isLoading: boolean;
    refetchPayees: () => void;
    payeesList: TPayee[];
}

const PayeesTable: FC<IProps> = ({ payeesList, refetchPayees, onAdd, isLoading }) => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'pay_bills.manage_payees'
    });
    const { placeholderLocale, submitLocale } = useLocales();
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const { handleDeletePayeeModal } = usePayBillsModal();

    const columns = [
        {
            label: 'Payee name',
            key: 'name',
            width: 250
        },
        {
            label: 'Nickname',
            key: 'alias'
        },
        {
            label: 'Account number',
            key: 'account_number'
        },
        {
            label: 'Scheduled bill payments',
            key: 'schedule_count'
        },
        {
            label: t('last_payment'),
            key: 'last_payment',
            render: (value?: string) => (value ? formatDate(value, 'MM/DD/YYYY') : '-')
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (_: undefined, data: TPayee) => (
                <div className={styles.table_actions}>
                    <Info variant="left" content="Pay a bill">
                        <Button
                            leftIcon="pay_bill"
                            onClick={() => navigate(routes.pay_bills_pay + `?payee=${data.id}`)}
                        />
                    </Info>
                    <Info variant="left" content="Edit">
                        <Button
                            leftIcon="edit"
                            onClick={() => navigate(routes.pay_bills_payees + `?payee=${data.id}`)}
                        />
                    </Info>
                    <Info variant="left" content="Delete">
                        <Button
                            leftIcon="trash"
                            className={styles.delete}
                            onClick={() =>
                                handleDeletePayeeModal(data.id, data.name, refetchPayees)
                            }
                        />
                    </Info>
                </div>
            )
        }
    ];

    const searchedPayees = useMemo(() => {
        return payeesList.filter((item) => {
            return (
                item.name.toLowerCase().includes(search.toLowerCase()) ||
                item?.alias?.toLowerCase()?.includes(search.toLowerCase()) ||
                item.account_number.toLowerCase().includes(search.toLowerCase())
            );
        });
    }, [search, payeesList]);

    return (
        <motion.div {...pageAnimation} className={styles.content}>
            <div className={styles.form_row}>
                <Input
                    leftIcon="search"
                    className={styles.search}
                    value={search}
                    onChange={(val) => setSearch(val)}
                    full
                    placeholder={placeholderLocale('search_payee_account')}
                    rightIcon={search.length > 0 ? 'filed_close' : undefined}
                    onClickRightIcon={() => setSearch('')}
                />
                <Button variant="primary" size="medium" onClick={onAdd}>
                    {submitLocale('add_new_payee')}
                </Button>
            </div>
            {isLoading ? (
                <>
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                </>
            ) : searchedPayees.length === 0 ? (
                <div className={styles.empty}>
                    <img src={emptyIllustration} alt="empty payees" />
                    <p className={styles.title}>No payees yet</p>
                    <p className={styles.description}>Payees you've added will appear here</p>
                    <Button variant="primary" size="medium" onClick={onAdd}>
                        {submitLocale('add_new_payee')}
                    </Button>
                </div>
            ) : (
                <Table
                    columns={columns}
                    rows={searchedPayees || []}
                    isActions
                    emptyDescription={t('empty_payees')}
                />
            )}
        </motion.div>
    );
};

export default PayeesTable;
